import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import Header from "../components/header/header";
import Footer from "../components/footer/footer";
import XanoAPI from "../lib/XanoAPI";
import Loader from "../components/loader/loader";

const KnowledgeBaseArticle: React.FC = () => {
  const [article, setArticle] = useState<KnowledgeBaseArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { slug } = useParams<{ slug: string }>();
  const api = XanoAPI.getInstance();

  const fetchArticle = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);

    try {
      const fetchedArticle = await api.getKnowledgeBaseArticle(slug);
      if (fetchedArticle) {
        setArticle(fetchedArticle);
      } else {
        setError("Article not found");
      }
    } catch (error) {
      setError("Failed to load the article. Please try again.");
      console.error("Error fetching article:", error);
    } finally {
      setLoading(false);
    }
  }, [api, slug]);

  useEffect(() => {
    fetchArticle();
  }, [fetchArticle]);

  return (
    <div className="flex-col md:flex">
      <Header />
      <main className="flex space-x-8 p-8 pt-6 container mt-16">
        {/* Main Article Content */}
        <section className="flex-1 space-y-4">
          {loading ? (
            <Loader />
          ) : error ? (
            <div>
              <h2 className="text-3xl font-bold tracking-tight">Error</h2>
              <p className="text-muted-foreground">{error}</p>
            </div>
          ) : article ? (
            <>
              <h1 className="text-4xl text-accent font-bold mb-4">
                {article.title}
              </h1>
              <p className="text-gray-700 mb-6">{article.summary}</p>
              <div
                className="prose lg:prose-xl"
                dangerouslySetInnerHTML={{ __html: article.content }}
              ></div>
            </>
          ) : (
            <div>
              <h2 className="text-3xl font-bold tracking-tight">
                Article Not Found
              </h2>
              <p className="text-muted-foreground">
                The requested article could not be found.
              </p>
            </div>
          )}
        </section>

        {/* Sidebar */}
        <aside className="w-64 hidden md:block bg-gray-100 rounded-lg p-4 sticky top-16 h-full">
          <h2 className="text-xl font-semibold mb-4 text-primary">
            Quick Links
          </h2>
          <ul className="space-y-2">
            <li>
              <a
                href="/knowledge-base"
                className="text-primary hover:underline"
              >
                Back to Knowledge Base
              </a>
            </li>
            <li>
              <a href="/contact" className="text-primary hover:underline">
                Contact Support
              </a>
            </li>
            <li>
              <a href="/about" className="text-primary hover:underline">
                About Us
              </a>
            </li>
          </ul>

          {/* Dynamic Call-to-Action */}
          {article?.call_to_action ? (
            <div
              className="mt-8 bg-primary text-white rounded-lg p-4 text-center"
              dangerouslySetInnerHTML={{ __html: article.call_to_action }}
            />
          ) : (
            <div className="mt-8 bg-primary text-white rounded-lg p-4 text-center">
              <h3 className="text-lg font-semibold mb-2">Need Help?</h3>
              <p className="text-sm mb-4">
                Get in touch with our support team for additional assistance.
              </p>
              <a
                href="/contact"
                className="inline-block bg-secondary text-primary font-semibold py-2 px-4 rounded hover:bg-accentGray transition"
              >
                Contact Us
              </a>
            </div>
          )}
        </aside>
      </main>
      <Footer />
    </div>
  );
};

export default KnowledgeBaseArticle;
